import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Download, PauseCircle, Pencil, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { TERMS_OF_SERVICE_VERSION } from '@/constants/legal';
import {
  getConsentHistory,
  getDsarRequests,
  recordConsent,
  submitDsar,
  type ConsentEvent,
} from '@/services/api.client';
import type { DsarType } from '@/types';
import { formatDate, statusBadgeClass } from '@/lib/utils';

// ─── Action card ──────────────────────────────────────────────────────────────

function ActionCard({
  icon,
  title,
  description,
  buttonLabel,
  onClick,
  disabled,
  variant = 'default',
}: {
  icon: ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'danger';
}) {
  const isDanger = variant === 'danger';
  return (
    <div
      className={`rounded-xl border p-4 flex items-start gap-4 ${
        isDanger
          ? 'border-[var(--status-red)] bg-[var(--status-red-tint)]'
          : 'border-[var(--bg-surface-raised)] bg-[var(--bg-surface)]'
      }`}
    >
      <div
        className={`size-10 rounded-md flex items-center justify-center shrink-0 ${
          isDanger ? 'bg-[var(--status-red)]/15' : 'bg-primary/15'
        }`}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{description}</p>
      </div>
      <Button
        size="sm"
        variant={isDanger ? 'destructive' : 'outline'}
        onClick={onClick}
        disabled={disabled}
        className="shrink-0"
      >
        {buttonLabel}
      </Button>
    </div>
  );
}

// ─── Submit-confirmation dialog (correction / restriction) ────────────────────

function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  placeholder,
  isPending,
  apiError,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  placeholder: string;
  isPending: boolean;
  apiError: string | null;
  onSubmit: (details: string) => void;
}) {
  const [details, setDetails] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setDetails('');
          setConfirmed(false);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="reason-details" className="text-sm">
              Details
            </Label>
            <Textarea
              id="reason-details"
              placeholder={placeholder}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={300}
              rows={3}
            />
            <p className="text-xs text-muted-foreground text-right">{details.length}/300</p>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="reason-confirm"
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(Boolean(v))}
              className="mt-0.5"
            />
            <label htmlFor="reason-confirm" className="text-sm cursor-pointer leading-relaxed">
              This request relates to my own personal data, and my contact details are current.
            </label>
          </div>
          {apiError && <p className="text-sm text-destructive">{apiError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(details)} disabled={!confirmed || isPending}>
            {isPending ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete account dialog (two-step) ─────────────────────────────────────────

function DeleteAccountDialog({
  open,
  onOpenChange,
  userEmail,
  isPending,
  apiError,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  isPending: boolean;
  apiError: string | null;
  onConfirm: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [understood, setUnderstood] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState('');

  const reset = () => {
    setStep(1);
    setUnderstood(false);
    setEmailConfirm('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--status-red-text)]">
            <AlertTriangle className="size-5" />
            Delete your account
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-[var(--status-red)] bg-[var(--status-red-tint)] p-3 text-sm space-y-2">
              <p className="font-medium text-[var(--text-primary)]">This will permanently:</p>
              <ul className="list-disc pl-5 space-y-1 text-xs text-[var(--text-secondary)]">
                <li>Close your Secure Labs account</li>
                <li>Revoke all your active visitor and recurring passes</li>
                <li>Delete your favourites and visit history</li>
                <li>Sign you out of all devices</li>
              </ul>
              <p className="text-xs text-[var(--text-secondary)] pt-1">
                We process the request within 30 days as required by Kenya Data Protection Act §26 —
                usually faster. Once it completes, this cannot be undone.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="delete-understood"
                checked={understood}
                onCheckedChange={(v) => setUnderstood(Boolean(v))}
                className="mt-0.5"
              />
              <label htmlFor="delete-understood" className="text-sm cursor-pointer leading-relaxed">
                I understand and want to delete my account.
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => setStep(2)} disabled={!understood}>
                Continue
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <p className="text-sm text-[var(--text-secondary)]">
              To confirm, type your email address below.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="delete-email" className="text-sm">
                Your email
              </Label>
              <Input
                id="delete-email"
                placeholder={userEmail}
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Type <span className="font-mono text-[var(--text-primary)]">{userEmail}</span>{' '}
                exactly.
              </p>
            </div>
            {apiError && <p className="text-sm text-destructive">{apiError}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} disabled={isPending}>
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirm}
                disabled={
                  emailConfirm.trim().toLowerCase() !== userEmail.toLowerCase() || isPending
                }
              >
                {isPending ? 'Submitting…' : 'Delete my account'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Consent history card ─────────────────────────────────────────────────────

/**
 * ConsentHistoryCard — surfaces the resident's standing consents in a
 * format the KDPA requires us to make accessible: which document, what
 * version, when they accepted it, and (where applicable) a way to
 * withdraw.
 *
 * Required-at-login consents (privacy/terms/age) cannot be withdrawn
 * standalone — withdrawal terminates the account, so the affordance
 * routes them to "Delete my account" instead.
 *
 * Marketing consent is fully opt-in/out from here.
 */
function ConsentHistoryCard() {
  const query = useQuery({
    queryKey: ['consent-history'],
    queryFn: () => getConsentHistory(),
  });

  const withdraw = useMutation({
    mutationFn: () =>
      recordConsent({
        scope: 'marketing_email',
        version: TERMS_OF_SERVICE_VERSION,
        granted: false,
      }),
    onSuccess: () => {
      void query.refetch();
    },
  });

  if (query.isPending) {
    return <Skeleton className="h-32 w-full" />;
  }
  if (!query.data) return null;

  const latestPerScope = (scope: string): ConsentEvent | undefined =>
    query.data
      .filter((c) => c.consent_type === scope)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];

  const privacy = latestPerScope('privacy_policy');
  const terms = latestPerScope('terms_of_service');
  const age = latestPerScope('age_18_plus');
  const marketing = latestPerScope('marketing_email');
  const visitorAttestations = query.data.filter(
    (c) => c.consent_type === 'visitor_data_lawful_basis' && c.granted,
  );

  const marketingActive = marketing?.granted === true;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Your consents</CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-border/50">
        <ConsentRow
          title="Privacy Policy"
          subtitle={
            privacy
              ? `Accepted ${formatDate(privacy.created_at)} · version ${privacy.version}`
              : 'No record found'
          }
          locked
        />
        <ConsentRow
          title="Terms of Service"
          subtitle={
            terms
              ? `Accepted ${formatDate(terms.created_at)} · version ${terms.version}`
              : 'No record found'
          }
          locked
        />
        <ConsentRow
          title="Confirmed I am 18 or older"
          subtitle={age ? `On ${formatDate(age.created_at)}` : 'No record found'}
          locked
        />
        <ConsentRow
          title="Product updates by email"
          subtitle={
            marketingActive
              ? `Opted in ${marketing ? formatDate(marketing.created_at) : ''}`
              : 'Not opted in'
          }
          action={
            marketingActive ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => withdraw.mutate()}
                disabled={withdraw.isPending}
              >
                {withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
              </Button>
            ) : (
              <Link
                to="/resident/account/notifications"
                className="text-sm underline underline-offset-4 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                Manage in notifications
              </Link>
            )
          }
        />
        {visitorAttestations.length > 0 && (
          <ConsentRow
            title="Visitor-data attestations"
            subtitle={`${visitorAttestations.length} recorded · most recent ${formatDate(
              visitorAttestations[0]?.created_at ?? '',
            )}`}
          />
        )}
      </CardContent>
      <CardContent className="border-t pt-3 text-xs text-muted-foreground">
        Withdrawing the Privacy Policy, Terms of Service, or 18-plus confirmation requires deleting
        your account. Use the danger zone above.
      </CardContent>
    </Card>
  );
}

function ConsentRow({
  title,
  subtitle,
  locked,
  action,
}: {
  title: string;
  subtitle: string;
  locked?: boolean;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="space-y-0.5 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      {action ?? (locked && <Badge className="shrink-0">Required</Badge>)}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PrivacyPage() {
  const { user } = useAuth();
  const location = useLocation();
  const userEmail = user?.email ?? '';

  // Derive the "back to account" link from the current path so this page
  // works correctly when mounted under any role surface.
  const backTo = location.pathname.replace(/\/privacy$/, '') || '/resident/account';

  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [openCorrection, setOpenCorrection] = useState(false);
  const [openRestriction, setOpenRestriction] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['dsar-requests'],
    queryFn: getDsarRequests,
  });

  const openRequest = existing?.find((r) => r.status === 'submitted' || r.status === 'processing');

  const submit = useMutation({
    mutationFn: (vars: { requestType: DsarType; details?: string }) =>
      submitDsar({
        requestType: vars.requestType,
        ...(vars.details ? { additionalDetails: vars.details } : {}),
      }),
    onSuccess: (result) => {
      setSubmittedRef(result.referenceNumber);
      setOpenCorrection(false);
      setOpenRestriction(false);
      setOpenDelete(false);
    },
  });

  const apiError =
    submit.isError && submit.error instanceof Object && 'message' in submit.error
      ? String((submit.error as { message: string }).message)
      : null;

  // ─── Submitted success view ────────────────────────────────────────────────
  if (submittedRef) {
    return (
      <div className="max-w-xl space-y-6">
        <div>
          <h1 className="text-2xl">Privacy & Data</h1>
        </div>
        <Card>
          <CardContent className="pt-8 pb-8 flex flex-col items-center text-center gap-4">
            <CheckCircle className="size-14 text-[var(--status-green-text)]" />
            <div className="space-y-1">
              <h2 className="text-lg">Request submitted</h2>
              <p className="text-sm text-muted-foreground font-mono">{submittedRef}</p>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              We'll email you at <span className="text-foreground">{userEmail}</span> within 30 days
              as required by Kenya Data Protection Act §26 — usually much sooner.
            </p>
            <Button asChild variant="outline">
              <Link to={backTo}>Back to account</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-2xl">Your data</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage the personal data we hold about you. We respond to every request within 30 days as
          required by Kenya Data Protection Act §26 — and usually much sooner.
        </p>
      </div>

      {/* What we hold */}
      <Card>
        <CardContent className="pt-4 pb-4 text-sm text-[var(--text-secondary)] leading-relaxed">
          <p className="mb-2 font-medium text-[var(--text-primary)]">What we hold about you</p>
          <p>
            Your profile (name, email, phone), your visit history at this estate, your active
            invitations and passes, your favourites, and the devices you've signed in with.
          </p>
        </CardContent>
      </Card>

      {/* Open request banner */}
      {loadingExisting ? (
        <Skeleton className="h-16 w-full" />
      ) : openRequest ? (
        <Card className="border-[var(--border-accent)] bg-[var(--estate-accent-tint)]">
          <CardContent className="pt-4 pb-4 space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">Open request</p>
              <Badge className={statusBadgeClass(openRequest.status)}>{openRequest.status}</Badge>
            </div>
            <p className="text-sm text-muted-foreground font-mono">{openRequest.referenceNumber}</p>
            <p className="text-sm text-muted-foreground">
              Submitted {formatDate(openRequest.submittedAt)}. You'll receive an email once it's
              resolved before you can submit another.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Action cards — only when no open request */}
      {!openRequest && (
        <>
          <div className="space-y-3">
            <ActionCard
              icon={<Download className="size-5 text-primary" />}
              title="Download a copy of my data"
              description="We'll email you a downloadable file of everything we hold about you."
              buttonLabel={
                submit.isPending && submit.variables?.requestType === 'access'
                  ? 'Requesting…'
                  : 'Request copy'
              }
              onClick={() => submit.mutate({ requestType: 'access' })}
              disabled={submit.isPending}
            />

            <ActionCard
              icon={<Pencil className="size-5 text-primary" />}
              title="Correct something wrong"
              description="Tell us what is inaccurate — your name, phone, or anything else — and we'll update it."
              buttonLabel="Open form"
              onClick={() => setOpenCorrection(true)}
            />

            <ActionCard
              icon={<PauseCircle className="size-5 text-primary" />}
              title="Limit how my data is used"
              description="Pause certain processing of your data while keeping your account active."
              buttonLabel="Open form"
              onClick={() => setOpenRestriction(true)}
            />
          </div>

          {/* Danger zone */}
          <div className="space-y-2 pt-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--status-red-text)]">
              Danger zone
            </p>
            <ActionCard
              variant="danger"
              icon={<Trash2 className="size-5 text-[var(--status-red-text)]" />}
              title="Delete my account"
              description="Permanently close your account and delete all your data. This cannot be undone."
              buttonLabel="Delete account"
              onClick={() => setOpenDelete(true)}
            />
          </div>
        </>
      )}

      {/* Past requests — terminal states only */}
      {existing &&
        existing.filter((r) => r.status === 'fulfilled' || r.status === 'failed').length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your past requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border/50">
                {existing
                  .filter((r) => r.status === 'fulfilled' || r.status === 'failed')
                  .map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-3">
                      <div className="space-y-0.5">
                        <p className="text-sm font-mono">{r.referenceNumber}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {r.requestType} · {formatDate(r.submittedAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={statusBadgeClass(r.status)}>{r.status}</Badge>
                        {r.status === 'fulfilled' && (
                          <span className="text-xs text-[var(--text-tertiary)]">
                            Sent via email
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Your consents — required + optional, with history */}
      <ConsentHistoryCard />

      {/* Privacy notice link */}
      <p className="text-center text-xs text-muted-foreground">
        <a href="/privacy" className="underline underline-offset-4 hover:text-primary">
          View our full Privacy Notice
        </a>
      </p>

      {/* Dialogs */}
      <ReasonDialog
        open={openCorrection}
        onOpenChange={setOpenCorrection}
        title="Correct something wrong"
        description="Tell us what's inaccurate and what it should be. We respond within 30 days as required by KDPA §26 — usually sooner."
        placeholder='e.g. "My phone number is now +254 700 000 000"'
        isPending={submit.isPending && submit.variables?.requestType === 'rectification'}
        apiError={apiError}
        onSubmit={(details) => submit.mutate({ requestType: 'rectification', details })}
      />

      <ReasonDialog
        open={openRestriction}
        onOpenChange={setOpenRestriction}
        title="Limit how my data is used"
        description="Tell us which processing you'd like to limit. We respond within 30 days as required by KDPA §26 — usually sooner."
        placeholder="e.g. Stop sharing my visits with the property manager."
        isPending={submit.isPending && submit.variables?.requestType === 'restriction'}
        apiError={apiError}
        onSubmit={(details) => submit.mutate({ requestType: 'restriction', details })}
      />

      <DeleteAccountDialog
        open={openDelete}
        onOpenChange={setOpenDelete}
        userEmail={userEmail}
        isPending={submit.isPending && submit.variables?.requestType === 'erasure'}
        apiError={apiError}
        onConfirm={() => submit.mutate({ requestType: 'erasure' })}
      />
    </div>
  );
}
